// LoginScreen.js
import { Alert, Button, Divider, Link, TextField } from "@mui/material";
import { blueGrey, grey } from "@mui/material/colors";
import { useStytch, useStytchSession } from "@stytch/react";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import useLocalStorageState from "use-local-storage-state";

import { isNil } from "lodash";
import DashboardLogo from "../assets/DashboardLogo";
import AnimatedCheckmark from "../components/feedback/AnimatedCheckmark/AnimatedCheckMark";
import InputWrapper from "../components/inputs/shared/InputWrapper";
import Flx from "../components/layout/Flx";
import Txt from "../components/typography/Txt";
import AuthLayout, { AuthGlassCard } from "../components/layout/AuthLayout";
import { ArrowLeft, ArrowLeftAlt, WestOutlined } from "@mui/icons-material";

const LoginScreen = () => {
  const { session } = useStytchSession();
  const stytch = useStytch();

  const [lsEmail, setLsEmail] = useLocalStorageState(
    "borrowerPortalLoginEmail",
    {
      defaultValue: null,
    },
  );
  const [email, setEmail] = useState(lsEmail);
  const navigate = useNavigate();
  const [status, setStatus] = useState("idle"); // "idle", "sending", "sent", "error"
  const [error, setError] = useState(null);
  const [remember, setRemember] = useState(isNil(lsEmail) ? false : true);

  const onRememberChange = (e) => {
    const value = e.target.checked;
    setRemember(value);
    if (!value) {
      setLsEmail(null);
    }
  };

  if (session) {
    return <Navigate to="/" replace />;
  }

  const handleSendMagicLink = async () => {
    setStatus("sending");
    if (remember) {
      setLsEmail(email);
    }

    try {
      await stytch.magicLinks.email.send(email.trim(), {
        login_magic_link_url: `${window.location.origin}/authenticate`,
        login_expiration_minutes: 10080,
        session_duration_minutes: 10080,
      });
      setStatus("sent");
      setError(null);
    } catch (err) {
      setError(err?.error_type);
      setStatus("idle");
    }
  };

  if (status === "sent") {
    return (
      <AuthGlassCard maxWidth={510}>
        <DashboardLogo width="145px" />
        <Flx fw column center g={2} sx={{ mt: 3 }}>
          <AnimatedCheckmark size={"64px !important"} sx={{ padding: 0 }} />
          <Flx fw wrap center g={0.5} sx={{ mt: 1 }}>
            <Txt center sx={{ mb: 2, px: 1 }}>
              A magic link has been sent to <b>{email}</b>. Please check your
              inbox and click the link to log in.
            </Txt>
          </Flx>
          <Flx gap={2} center sx={{ mt: 4 }}>
            <Link
              underline="none"
              onClick={() => setStatus("idle")}
              role="button"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                fontFamily: "var(--ibm)",
                color: "#2962ff",
                fontWeight: 400,
                fontSize: "13px",
                cursor: "pointer",
              }}
            >
              <WestOutlined />
              Back to Login Page
            </Link>
            <Divider
              orientation="vertical"
              flexItem
              sx={{ borderColor: grey[400] }}
            />
            <Flx center g={1}>
              <OutlookSvg />
              <Link
                underline="none"
                href="https://outlook.office.com/mail/0/inbox"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontFamily: "var(--ibm)",
                  color: "#2962ff",
                  fontWeight: 400,
                  fontSize: "13px",
                }}
              >
                Open in Outlook
              </Link>
            </Flx>
          </Flx>
        </Flx>
      </AuthGlassCard>
    );
  }

  return (
    <AuthLayout
      title="Borrower Log In"
      description="Please enter your email address below to login."
      cardWidth={460}
    >
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, width: "100%" }}
          onClose={() => setError("")}
        >
          {error === "email_not_found"
            ? "We could not find an account associated with that email address. Please try again or create a new account."
            : "Oops, something went wrong. Please try again."}
        </Alert>
      )}

      <InputWrapper sx={{ mb: 2, width: "100%" }}>
        <TextField
          placeholder="Enter your email address..."
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          disabled={status === "sending" || status === "sent"}
          sx={{ mb: 1 }}
        />
      </InputWrapper>

      <Button
        variant="contained"
        fullWidth
        size="large"
        loading={status === "sending"}
        onClick={handleSendMagicLink}
        disabled={
          !email || status === "sending" || status === "sent" || isNil(email)
        }
      >
        Send Magic Link
      </Button>

      <Flx
        center
        wrap
        gap={0.5}
        sx={{
          mt: 3,
          width: "100%",
        }}
      >
        <Txt>New to {process.env.REACT_APP_ORGANIZATION_NAME || "us"}?</Txt>
        <Link
          onClick={() => navigate("/sign-up")}
          sx={{ textDecoration: "none", cursor: "pointer" }}
        >
          <Txt>Create an account</Txt>
        </Link>
      </Flx>
    </AuthLayout>
  );
};

// ─── Outlook SVG ────────────────────────────────────────────────────────────
const OutlookSvg = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      version="1.1"
      style={{ width: "16px" }}
      viewBox="0 0 103.17322 104.31332"
      xmlSpace="preserve"
    >
      <defs id="defs43"></defs>
      <path
        d="m 64.566509,22.116383 v 20.404273 l 7.130526,4.489881 c 0.188058,0.05485 0.595516,0.05877 0.783574,0 L 103.16929,26.320259 c 0,-2.44867 -2.28412,-4.203876 -3.573094,-4.203876 H 64.566509 z"
        style={{ fill: "rgb(0, 114, 198)" }}
      />
      <path
        d="m 64.566509,50.13308 6.507584,4.470291 c 0.916782,0.673874 2.021622,0 2.021622,0 -1.100922,0.673874 30.077495,-20.035993 30.077495,-20.035993 v 37.501863 c 0,4.082422 -2.61322,5.794531 -5.551621,5.794531 H 64.562591 V 50.13308 z"
        style={{ fill: "rgb(0, 114, 198)" }}
      />
      <g transform="matrix(3.9178712,0,0,3.9178712,-13.481403,-41.384473)">
        <path
          d="m 11.321,20.958 c -0.566,0 -1.017,0.266 -1.35,0.797 -0.333,0.531 -0.5,1.234 -0.5,2.109 0,0.888 0.167,1.59 0.5,2.106 0.333,0.517 0.77,0.774 1.31,0.774 0.557,0 0.999,-0.251 1.325,-0.753 0.326,-0.502 0.49,-1.199 0.49,-2.09 0,-0.929 -0.158,-1.652 -0.475,-2.169 -0.317,-0.516 -0.75,-0.774 -1.3,-0.774 z"
          style={{ fill: "rgb(0, 114, 198)" }}
        />
        <path
          d="m 3.441,13.563 v 20.375 l 15.5,3.25 V 10.563 l -15.5,3 z m 10.372,13.632 c -0.655,0.862 -1.509,1.294 -2.563,1.294 -1.027,0 -1.863,-0.418 -2.51,-1.253 C 8.094,26.4 7.77,25.312 7.77,23.97 c 0,-1.417 0.328,-2.563 0.985,-3.438 0.657,-0.875 1.527,-1.313 2.61,-1.313 1.023,0 1.851,0.418 2.482,1.256 0.632,0.838 0.948,1.942 0.948,3.313 10e-4,1.409 -0.327,2.545 -0.982,3.407 z"
          style={{ fill: "rgb(0, 114, 198)" }}
        />
      </g>
    </svg>
  );
};

export default LoginScreen;
